import { z } from "zod"

import type { DropdownItem } from "#ui/types"
import {
  BaseContractSchema,
  ContractSchema,
  DocKind,
  GSKpiLevel,
  GSRefreshModel,
  GaugeModel,
  GroupGSKpi,
  GroupGSKpiUuid,
  InvoiceSchema,
  InvoiceSchemaShort,
  SiteGSKpi,
  SiteSchema,
  SiteSchemaOverview,
  SubGroupGSKpi,
  SubGroupGSKpiUuid,
  User,
  WindTurbineSchema,
  SolarFieldSchema,
  HydroTurbineSchema,
  CogenerationSchema,
  ConsumerSchema,
  BatterySchema,
} from "~/composables/codegen/api-client"
import type { ArtifactLogSchema, EventSchema, NoteSchema } from "~/composables/codegen/api-client"

// _______ General _______

export const Unit = z.enum([
  // Energy
  "TWh",
  "GWh",
  "MWh",
  "kWh",
  // Time
  "h",
  //
  // Reactive Power
  "kVA",
  "V",
  "kVArh",
  //
  // Irradiance
  "W/m²",
  "Wh/m²",
  // Power
  "TW",
  "GW",
  "MW",
  "kW",
  "W",
  //
  // Prices
  "eur/kW",
  "eur/MW",
  "eur/kWh",
  "eur/MWh",
  "eur/h",
  "eur/eur",
  "ceur/kWh",
  //
  // Currency
  "ceur", // centi euro
  "eur",
  "keur", // kilo euro -> milliers
  "Meur", // mega euro -> million
  "Geur", // giga euro -> milliard
  //
  // Distance
  "m", // meter
  //
  // Special
  "dimensionless",
  "eur/dimensionless",
  "%",
  "latlng",
  "coef_l",
])
export type Unit = z.infer<typeof Unit>

export const InvoiceMetric = z.enum(["count", Unit.enum.keur, Unit.enum.Meur])
export type InvoiceMetric = z.infer<typeof InvoiceMetric>

export const Freq = z.enum(["1YS", "1MS", "1W-MON", "1D", "1h", "30min", "15min", "10min", "5min"])
export type Freq = z.infer<typeof Freq>

// _______ Backend Validation error  _______
export const BackendValidationErrorDetail = z.object({
  loc: z.string().array(),
  msg: z.string(),
})

export type BackendValidationErrorDetail = z.infer<typeof BackendValidationErrorDetail>

export const BackendValidationError = z.object({ detail: BackendValidationErrorDetail.array() })
export type BackendValidationError = z.infer<typeof BackendValidationError>

// _______ Timeseries Models _______

export const TimeSeries = z.object({
  name: z.string(),
  index: z.array(z.coerce.date().or(z.number())),
  data: z.array(z.number().nullable()),
})
export type TimeSeries = z.infer<typeof TimeSeries>

export const DataFrame = z.object({
  columns: z.array(z.string()),
  index: z.array(z.coerce.date().or(z.number())),
  data: z.array(z.array(z.number().nullable())),
})
export type DataFrame = z.infer<typeof DataFrame>

// _______ Invoice _______

export const InvoiceActionCategory = z.enum([
  "download",
  "link",
  "base",
  "submit",
  "upload",
  "cancel",
])
export type InvoiceActionCategory = z.infer<typeof InvoiceActionCategory>

export const InvoiceActionId = z.enum([
  "download_pdf",
  "download_excel",
  "view_invoice",
  "view_site",
  "view_contract",
  "compute",
  "set_as_computed",
  "submit",
  "publish",
  "publish_draft",
  "unpublish",
  "credit_note",
  "delete",
  "edit",
  "set_as_submitted",
  "upload_attachment",
  "set_as_paid",
  "set_as_accounted",
  "set_as_validated",
  "set_to_waiting_for_publication",
  "unpublish_waiting",
])
export type InvoiceActionId = z.infer<typeof InvoiceActionId>
export interface InvoiceActionItem extends DropdownItem {
  action: InvoiceActionId
}

// _______ Metric _______

export const MetricName = z.enum([
  "contracted_capacity",
  "data_health",
  "financial_report",
  "production_report",
  "power_production",
  "revenue",
  "incident_energy_when_energy",
  "availability_rate",
  "trs",
  "pr",
  "portfolio_health",
])
export type MetricName = z.infer<typeof MetricName>

export const MetricModel = z.enum(["assetmanager", "assetowner", "holding", "site"])
export type MetricModel = z.infer<typeof MetricModel>

export const MetricHorizon = z.enum([
  "ytd",
  "yt_last_month",
  "yt_quarter",
  "last_year",
  "rolling_year",
  "current_month",
  "last_month",
  "rolling_month",
  "current_week",
  "last_week",
  "rolling_week",
  "today",
  "yesterday",
])
export type MetricHorizon = z.infer<typeof MetricHorizon>

export const AvailabilityType = z.enum(["injection", "detailed", "ir_prorated"])
export type AvailabilityType = z.infer<typeof AvailabilityType>

export interface AvailabilityGauge {
  last_data: string | Date | null
  status: "good" | "late" | "missing" | null
}

export interface ConsistencyGauge {
  consistent: boolean
  coverage: number
  difference: number
}

export interface DataHealthFieldInfo {
  tags: TSTag[]
  sprovider: { name: string; code: string } | null
  freq: Freq
  unit: Unit
}

export interface DataHealthField {
  availability: {
    [key: string]: AvailabilityGauge
  }
  completeness: {
    [key: string]: GaugeModel
  }
  consistency: {
    [key: string]: ConsistencyGauge
  }
  infos: {
    [key: string]: DataHealthFieldInfo
  }
  reference: string
}

//

export type * from "./models"
export type * from "./client"

export const ProducerCompanyKind = z.enum(["assetmanager", "assetowner", "holding"])
export type ProducerCompanyKind = z.infer<typeof ProducerCompanyKind>

export const NoteQueryModelName = z.enum([
  "site",
  "assetmanager",
  "assetowner",
  "holding",
  "contract",
  "portfolio",
  "invoice",
])
export type NoteQueryModelName = z.infer<typeof NoteQueryModelName>

export const NoteTagLevel = z.enum(["info", "warning", "error", "action"])
export type NoteTagLevel = z.infer<typeof NoteTagLevel>

export const ReportTargetModel = z.enum(["assetmanager", "assetowner", "holding", "site"])
export type ReportTargetModel = z.infer<typeof ReportTargetModel>

export const ReportTargetSchema = z.object({
  uuid: z.string().uuid(),
  model: ReportTargetModel,
  name: z.string(),
  disabledDocGenKinds: z.record(z.string(), z.boolean()).optional(),
})
export type ReportTargetSchema = z.infer<typeof ReportTargetSchema>

export const GSRefreshTargetSchema = z.object({
  uuid: z.string().uuid(),
  model: GSRefreshModel,
  name: z.string(),
})
export type GSRefreshTargetSchema = z.infer<typeof GSRefreshTargetSchema>

export const BudgetScope = z.enum(["operational", "business_plan"])
export type BudgetScope = z.infer<typeof BudgetScope>

// GSMetricName from zodios is z.unknown for now... So type it ourselves
export const GSMetricName = z.enum([
  "revenue",
  "production",
  "availability_injection",
  "availability_detailed",
  "availability_energy_prorated",
  "availability_technical",
  "pr",
  "pr_operational_attained",
  "pr_operational_estimated",
  "pr_business_plan_estimated",
  "trs",
  "trs_operational_attained",
  "trs_operational_estimated",
  "trs_business_plan_estimated",
  "self_consumption_rate",
  "self_production_rate",
  "coverage_rate",
])
export type GSMetricName = z.infer<typeof GSMetricName>

export const GSFreq = z.enum([Freq.enum["1YS"], Freq.enum["1MS"], Freq.enum["1D"]])
export type GSFreq = z.infer<typeof GSFreq>

// Front-only horizons whose range depends on a business object (not part of the backend HorizonLabel)
export const CustomHorizonLabel = z.enum(["contract_year"])
export type CustomHorizonLabel = z.infer<typeof CustomHorizonLabel>

// Budget percentile used by the GS budget columns and budget gauges
export const BudgetPercentile = z.enum(["budget_default", "p50", "p90"])
export type BudgetPercentile = z.infer<typeof BudgetPercentile>

// _______ DataHealth _______

export const DHStatus = z.enum(["good", "warning", "critical", "missing"])
export type DHStatus = z.infer<typeof DHStatus>

export const DHCommentLevel = z.enum(["error", "info", "aggregated"])
export type DHCommentLevel = z.infer<typeof DHCommentLevel>

export type InvoiceComputeTarget =
  | BaseContractSchema
  | ContractSchema
  | SiteSchemaOverview
  | SiteSchema
  | InvoiceSchemaShort
  | InvoiceSchema

// Missing distiction in backend for DocKind which have are document generated
export const DocGenKind = z.enum([
  DocKind.enum.finance_report,
  DocKind.enum.operation_report,
  DocKind.enum.performance_test_report,
  DocKind.enum.pr_report,
  DocKind.enum.cumulative_index_report,
  DocKind.enum.data_extract,
  DocKind.enum.invoice_lines_report,
  DocKind.enum.invoices_accounting_report,
  DocKind.enum.contract_data_extract,
  DocKind.enum.directory_company_info,
  DocKind.enum.directory_contact_info,
  DocKind.enum.finance_landing_extract,
  DocKind.enum.finance_extract,
  DocKind.enum.performance_tests_extract,
  DocKind.enum.commissioning_reserves_extract,
  DocKind.enum.technical_data_extract,
  DocKind.enum.budget_extract,
  DocKind.enum.neg_price_compensation_extract,
  DocKind.enum.invoicing_request,
])

export type DocGenKind = z.infer<typeof DocGenKind>

export type DocFileExtension = "xlsx" | "pdf"

// Mirrors `DocKind.to_extension` in the backend
// (zefire/logic/docgen/document_reliant.py): spreadsheet-style extracts/reports
// are rendered as xlsx, everything else defaults to pdf.
export const mapDocGenKindExtension = {
  [DocKind.enum.finance_report]: "pdf",
  [DocKind.enum.operation_report]: "pdf",
  [DocKind.enum.performance_test_report]: "pdf",
  [DocKind.enum.pr_report]: "xlsx",
  [DocKind.enum.cumulative_index_report]: "xlsx",
  [DocKind.enum.data_extract]: "xlsx",
  [DocKind.enum.invoice_lines_report]: "xlsx",
  [DocKind.enum.invoices_accounting_report]: "xlsx",
  [DocKind.enum.contract_data_extract]: "xlsx",
  [DocKind.enum.directory_company_info]: "xlsx",
  [DocKind.enum.directory_contact_info]: "xlsx",
  [DocKind.enum.finance_landing_extract]: "xlsx",
  [DocKind.enum.finance_extract]: "xlsx",
  [DocKind.enum.performance_tests_extract]: "xlsx",
  [DocKind.enum.commissioning_reserves_extract]: "xlsx",
  [DocKind.enum.technical_data_extract]: "xlsx",
  [DocKind.enum.budget_extract]: "xlsx",
  [DocKind.enum.neg_price_compensation_extract]: "xlsx",
  [DocKind.enum.invoicing_request]: "xlsx",
} satisfies Record<DocGenKind, DocFileExtension>

export const DocUploadKind = z.enum([DocKind.enum.contract, DocKind.enum.invoice_attachment])
export type DocUploadKind = z.infer<typeof DocUploadKind>

export const GSKpiAggregationCat = z.enum(["company", "region", "technology"])
export type GSKpiAggregationCat = z.infer<typeof GSKpiAggregationCat>

export const GSKpi = z.union([
  SiteGSKpi,
  GroupGSKpiUuid,
  SubGroupGSKpiUuid,
  GroupGSKpi,
  SubGroupGSKpi,
])
export type GSKpi = z.infer<typeof GSKpi>
export const CompanyGSKpi = GroupGSKpiUuid.extend({
  target_model: z.enum([GSKpiLevel.enum.assetowner, GSKpiLevel.enum.holding]),
})
export type CompanyGSKpi = z.infer<typeof CompanyGSKpi>

export const Scope = User.shape.permissions.element.options[0]
export type Scope = z.infer<typeof Scope>

export type DropdownItemScope = DropdownItem & { scope?: Scope }

export const AssetSchema = z.union([
  WindTurbineSchema,
  SolarFieldSchema,
  HydroTurbineSchema,
  CogenerationSchema,
  ConsumerSchema,
  BatterySchema,
])
export type AssetSchema = z.infer<typeof AssetSchema>

export const InvoiceCancelActions = z.enum(["unpublish", "credit_note", "delete"])
export type InvoiceCancelActions = z.infer<typeof InvoiceCancelActions>

export const InvoicesQueryModel = z.enum([
  "assetmanager",
  "assetowner",
  "holding",
  "site",
  "contract",
])
export type InvoicesQueryModel = z.infer<typeof InvoicesQueryModel>

export const ComparisonHorizonType = z.enum(["last_year", "preceding", "none"])
export type ComparisonHorizonType = z.infer<typeof ComparisonHorizonType>

export type ArtifactLDType = ArtifactLogSchema["data"]["type"]

// Events

export type EventSchemaWithSilence = EventSchema & {
  notes: NoteSchema[]
  silence_notes: NoteSchema[]
}

// Reactive
//
export const ReactiveSetpointTypes = z.enum(["local_voltage_regulation", "tanphi"])
export type ReactiveSetpointTypes = z.infer<typeof ReactiveSetpointTypes>

export const RevenueStatus = z.enum(["paid", "validated", "computed", "not_contracted"])
export type RevenueStatus = z.infer<typeof RevenueStatus>

// From Codegen Client:
export {
  PastillaSchema,
  PastillaName,
  HorizonLabel,
  PastillaStatus,
  DocumentSchemaShort,
  EventSchema,
  EventSchemaUpdate,
  EventType,
  EventCause,
  EventSource,
  EventLosses,
  EnergyEstimationMode,
  DeliveryPointCodeKind,
  DeliveryPointType,
  DeliveryPointSchema,
  DeliveryPointSchemaUpdate,
  DPContractualMappingSchema,
  Segment as MeterSegment,
  EnedisNetworkCfg,
  DSOSchema,
  MachineSubType as MachineType,
  MachineType as MachineTechno,
  TurbineType as WindTurbineType,
  WindTurbineSchema,
  SolarMountingType,
  SolarTrackingType,
  OrientationReference,
  SolarFieldSchema,
  HydroType as HydroTurbineType,
  HydroTurbineSchema,
  CogeType as CogenerationType,
  CogenerationSchema,
  Inverter,
  Module,
  TechnicalData,
  SiteStatus,
  SiteType,
  SiteContractStatus,
  PastillaSchemaShort,
  SiteSchema,
  SiteSchemaUpdate,
  SiteSchemaOverview,
  SiteSchemaShort,
  BudgetSchema,
  DocumentQueryModelName,
  DocKind,
  NoKwargsReport,
  GaugeModel,
  GSGaugeMetric,
  SiteGSKpi,
  GroupGSKpi,
  GroupGSKpiUuid,
  SubGroupGSKpi,
  SubGroupGSKpiUuid,
  GSKpiLevel,
  GSKpiTechno,
  GSRefreshModel,
  GSDataCategory,
  DataExtractCategory,
  GSFinanceView,
  DateRangeReportKwargs,
  DataExtractReportKwargs,
  CumIndexReportKwargs,
  OperationReportKwargs,
  FinanceReportKwargs,
  FinanceExtractReportKwargs,
  CompanySchemaShort,
  CounterpartySummarySchema,
  ContactInfoSchema,
  ContactInfoWithRolesSchema,
  ContractStatus,
  ContractTypeSchema,
  ClauseSchema,
  ClauseTypes,
  ClauseProduct,
  InvoicingCfg,
  InvoicingCfgDetails,
  InvoicingFreq,
  PriceMechanism,
  TargetGranularity,
  ContractStatusHistorySchema,
  BaseContractSchema,
  ContractSchema,
  ContractSummarySchema,
  ContractSchemaUpdate,
  User,
  InvoiceStatus,
  InvoiceSubmitChannel,
  InvoiceExternalStatus,
  InvoiceLineSchema,
  InvoiceLogAction,
  InvoiceSource,
  RegulationFilter,
  InvoiceContractSchema,
  InvoiceSchema,
  InvoiceSchemaShort,
  InvoiceSiteSchemaShort,
  InvoiceComputationSchema,
  FilterKind,
  NoteTagSchema,
  NoteTagSchemaCreate,
  NoteTagSchemaUpdate,
  NoteTarget,
  NoteSchema,
  NoteSchemaCreate,
  NoteSchemaUpdate,
  BaseDHSchema,
  DeliveryPointKind,
  SiteDHSchema,
  DHCategory,
  DHCheckKind,
  DHSchema,
  Way,
  MeasureType,
  IndexPriceFallback,
  DayAheadPricesSource,
  DHFlagCode,
  CompanyKind,
  CommissioningSubgroupName,
  CommissioningGroupName,
  CommissioningSite,
  SiteStatusHistorySchema,
  ArtifactSchema,
  ArtifactLogSchema,
  ArtifactStatus,
  ArtifactKind,
  ALDSerieDataSchema,
  TSMetadataOverview,
  TSMetadataTags,
  SiteMetering,
  SiteCollectedTimeseries,
  SiteEditableTimeseries,
  TimeseriesGroup,
  TSAcquisitionGroup,
  TSSchema,
  EditTimeseriesData,
  EditTimeseriesDataRecord,
  TSWay,
  TSReadableName,
  TSTargetModel,
  ReactiveSetpointLocalVoltageRegulation,
  ReactiveSetpointTanPhi,
  ReactiveSetpointDirective,
  ReactiveChartDataOnPeriod,
  InvoicingGroup,
  InvoicingSubgroup,
  GroupInvoiceStatus,
  PaymentDelay,
  OnHoldReason,
  InvoicesToRecoverSchema,
  InvoicesToRecoverContractSchema,
  FeedInTarifSolar,
  ProrataTemporisMode,
  InvoicingMode,
  FTClauseThresholdMode,
  NegativePrice,
  PriceType,
  ContactEmailSchema,
  EmailContactsSchema,
  PortfolioSchema,
  SpotManagementFee,
  CfdMajoration,
  CfdCr16,
  CfdCr17,
  PenaltyPriceFilter,
  CieScope,
  UIFeature,
  StaggeringGroup,
} from "~/composables/codegen/api-client"
